import get from 'lodash.get';
import { usePreferencesStore } from '@/stores/Preferences.store';
import {READY_GATE} from '@/services/bootstrap';

export let DEFAULT_PREFERENCES = {
  general: {
    locale: 'en-US',
    unitSystem: 'Metric',         //'Metric' or 'Imperial'
    paperUsage: 'EquivalentPages' //'EquivalentPages' or 'SquareArea'
  }
};

export default function preferencesServiceFactory(services) {
  let thisService = {
    init,
    initAsync,
    getPreferences,
    getPreferencesAsync,
    getPreference,
    getPreferenceAsync,
    getOrgPreference,
    getOrgPreferenceAsync,
    getOrgTypePreference,
    getOrgTypePreferenceAsync,
    setPreferenceAsync,
    setOrgPreferenceAsync,
    setOrgTypePreferenceAsync,
    deletePreferenceAsync,
    deleteOrgPreferenceAsync,
    deleteOrgTypePreferenceAsync,
    migratePreferenceAsync,
    migrateOrgPreferenceAsync,
    migrateOrgTypePreferenceAsync,
    getCookie,
    getCookieObject,
    setCookie,
    setCookieObject,
    deleteCookie,
    getLocalStorage,
    getLocalStorageObject,
    setLocalStorage,
    setLocalStorageObject,
    deleteLocalStorage,
  };

  let preferencesStore;

  return thisService;

  ////////////////////
  function init() {
    // console.log('Preferences Service: Start');
    DEFAULT_PREFERENCES.locale = getDefaultLanguage();
    preferencesStore = usePreferencesStore();

    // We moved local preferences to localStorage
    deleteCookie('pros-preferences');

    let preferences = Object.assign({}, DEFAULT_PREFERENCES);
    if (services.session.isUser()) {
      let ccPreferences = services.session.getClientConfig().preferences.entity;
      let missing = false;
      if (!ccPreferences.general) { ccPreferences.general = {}; }
      if (!ccPreferences.general.locale) { missing = true; ccPreferences.general.locale = DEFAULT_PREFERENCES.general.locale; }
      if (!ccPreferences.general.unitSystem) { missing = true; ccPreferences.general.unitSystem = DEFAULT_PREFERENCES.general.unitSystem; }
      if (!ccPreferences.general.paperUsage) { missing = true; ccPreferences.general.paperUsage = DEFAULT_PREFERENCES.general.paperUsage; }
      if (missing) {
        setPreferenceAsync('general', ccPreferences.general).then(updatedPrefs => {
          services.bootstrap.readyGateCleared(READY_GATE.preferences);
        });
      }

      preferencesStore.setPreferences(ccPreferences);
      services.bootstrap.readyGateCleared(READY_GATE.preferences);
      return ccPreferences;
    }
    else {
      let cookiePreferences = getLocalStorageObject('pros-preferences', preferences);
      if (!cookiePreferences.general) { cookiePreferences.general = {}; }
      if (!cookiePreferences.general.locale) { cookiePreferences.general.locale = DEFAULT_PREFERENCES.general.locale; }
      if (!cookiePreferences.general.unitSystem) { cookiePreferences.general.unitSystem = DEFAULT_PREFERENCES.general.unitSystem; }
      if (!cookiePreferences.general.paperUsage) { cookiePreferences.general.paperUsage = DEFAULT_PREFERENCES.general.paperUsage; }

      setLocalStorageObject('pros-preferences', cookiePreferences);
      preferencesStore.setPreferences(cookiePreferences);
      services.bootstrap.readyGateCleared(READY_GATE.preferences);
      return cookiePreferences;
    }
  }
  function initAsync() {
    // console.log('Preferences Service: Start');
    DEFAULT_PREFERENCES.locale = getDefaultLanguage();
    preferencesStore = usePreferencesStore();

    // We moved local preferences to localStorage
    deleteCookie('pros-preferences');

    let preferences = Object.assign({}, DEFAULT_PREFERENCES);
    if (services.session.hasEntity()) {
      return getPreferencesAsync().then(resp => {
        let myPreferences = {...resp};
        let missing = false;
        if (!myPreferences.general) { myPreferences.general = {}; }
        if (!myPreferences.general.locale) { missing = true; myPreferences.general.locale = DEFAULT_PREFERENCES.general.locale; }
        if (!myPreferences.general.unitSystem) { missing = true; myPreferences.general.unitSystem = DEFAULT_PREFERENCES.general.unitSystem; }
        if (!myPreferences.general.paperUsage) { missing = true; myPreferences.general.paperUsage = DEFAULT_PREFERENCES.general.paperUsage; }
        if (missing) {
          return setPreferenceAsync('general', myPreferences.general).then(updatedPrefs => {
            services.bootstrap.readyGateCleared(READY_GATE.preferences);
          });
        }
        else {
          preferencesStore.setPreferences(myPreferences);
          services.bootstrap.readyGateCleared(READY_GATE.preferences);
          return myPreferences;
        }
      });
    }
    else {
      let cookiePreferences = getLocalStorageObject('pros-preferences', preferences);
      if (!cookiePreferences.general) { cookiePreferences.general = {}; }
      if (!cookiePreferences.general.locale) { cookiePreferences.general.locale = DEFAULT_PREFERENCES.general.locale; }
      if (!cookiePreferences.general.unitSystem) { cookiePreferences.general.unitSystem = DEFAULT_PREFERENCES.general.unitSystem; }
      if (!cookiePreferences.general.paperUsage) { cookiePreferences.general.paperUsage = DEFAULT_PREFERENCES.general.paperUsage; }

      setLocalStorageObject('pros-preferences', cookiePreferences);
      preferencesStore.setPreferences(cookiePreferences);
      services.bootstrap.readyGateCleared(READY_GATE.preferences);
      return Promise.resolve(cookiePreferences);
    }
  }

  function getDefaultLanguage() {
    let cc = services.session.getClientConfig();
    if (!cc) {
      return 'en-US';
    }

    const localeOptions = cc.preferences?.options?.locales ? cc.preferences.options.locales : [];
    let found = localeOptions.find(lo => {
      return lo.value.toLowerCase().startsWith(window.navigator.language.toLowerCase());
    });

    if (!found) {
      found = localeOptions.find(lo => {
        return lo.value.toLowerCase().startsWith(window.navigator.language.split('-')[0].toLocaleLowerCase());
      });
    }

    return found ? found.value : 'en-US';
  }

  function getPreferencesAsync(refresh = false) {
    const cachedPreferences = preferencesStore.preferences;
    if (!refresh && cachedPreferences) {
      return Promise.resolve(cachedPreferences);
    }

    let config = {
      version: 2,
    };

    if (services.session.hasEntity()) {
      return services.http.get('portal_service', 'preferences', config).then(resp => {
        preferencesStore.setPreferences(resp.data);
        return resp.data;
      })
        .catch(() => {
          // It can be normal to fail to get preferences (anonymous users for example)
          // In this case we should try to get preferences from the browser cookie
          const tempPrefs = getLocalStorageObject('pros-preferences', DEFAULT_PREFERENCES);
          preferencesStore.setPreferences(tempPrefs);
          return Promise.resolve(tempPrefs);
        });
    }
    else {
      return Promise.resolve(getLocalStorageObject('pros-preferences'));
    }
  }

  function getPreferences() {
    return preferencesStore.preferences;
  }

  function migratePreferenceAsync(oldPath, oldContext = null, newPath, newContext = null) {
    let currentValue = getPreference(oldPath, oldContext);
    if (!currentValue) {
      return Promise.resolve(preferencesStore.preferences);
    }

    return setPreferenceAsync(newPath, currentValue, newContext).then(() => {
      return deletePreferenceAsync(oldPath, oldContext);
    });
  }

  function migrateOrgPreferenceAsync(oldPath, oldContext = null, newPath, newContext = null) {
    return migratePreferenceAsync(getPathWithOrg(oldPath), oldContext, getPathWithOrg(newPath), newContext);
  }

  function migrateOrgTypePreferenceAsync(oldPath, oldContext = null, newPath, newContext = null) {
    return migratePreferenceAsync(getPathWithOrgType(oldPath), oldContext, getPathWithOrgType(newPath), newContext);
  }

  function getPreference(path, context = null, withOrg = false, withOrgType = false) {
    let myPath = withOrg ? getPathWithOrg(path) : path;
    myPath = withOrgType ? getPathWithOrgType(myPath) : myPath;
    myPath = getPathWithContext(myPath, context);

    const cachedPreferences = JSON.parse(JSON.stringify(preferencesStore.preferences));
    return get(cachedPreferences, myPath);
  }

  function getPreferenceAsync(path, context = null, withOrg = false, withOrgType = false) {
    let config = {
      cache: 'refresh',
      version: 2,
    };

    let myPath = withOrg ? getPathWithOrg(path) : path;
    myPath = withOrgType ? getPathWithOrgType(myPath) : myPath;
    myPath = getPathWithContext(myPath, context);

    let fullPath = `preferences/${myPath}`;
    if (services.session.hasEntity()) {
      return services.http.get('portal_service', fullPath, config).then(resp => {
        preferencesStore.setPreference(path, resp.data);
        return resp.data;
      })
        .catch(() => {
          return Promise.resolve(null);
        });
    }
    else {
      let cookiePreferences = getLocalStorageObject('pros-preferences', DEFAULT_PREFERENCES);
      return Promise.resolve(get(cookiePreferences, myPath, null));
    }
  }

  function getOrgPreference(path, context = null) {
    return getPreference(path, context, true, false);
  }

  function getOrgPreferenceAsync(path, context = null) {
    return getPreferenceAsync(path, context, true, false);
  }

  function getOrgTypePreference(path, context = null) {
    return getPreference(path, context, false, true);
  }

  function getOrgTypePreferenceAsync(path, context = null) {
    return getPreferenceAsync(path, context, false, true);
  }

  function setPreferenceAsync(path, value, context = null, withOrg = false, withOrgType = false) {
    let myPath = withOrg ? getPathWithOrg(path) : path;
    myPath = withOrgType ? getPathWithOrgType(myPath) : myPath;
    myPath = getPathWithContext(myPath, context);

    let fullPath = `preferences/${myPath}`;
    if (services.session.hasEntity()) {
      return services.http.patch('portal_service', fullPath, JSON.stringify(value), {
        version: 2,
        headers: {'Content-Type': 'application/json'}
      }).then(resp => {
        setLocalStorageObject('pros-preferences', resp.data);
        preferencesStore.setPreferences(resp.data);
        return resp.data;
      })
        .catch(e => {
          console.warn('Preference failed to set', path, value, context, withOrg, withOrgType, myPath);
          const cachedPreferences = preferencesStore.preferences;
          return Promise.resolve(JSON.parse(JSON.stringify(cachedPreferences)));
        });
    }
    else {
      let cookiePreferences = getLocalStorageObject('pros-preferences', DEFAULT_PREFERENCES);
      services.helpers.set(cookiePreferences, myPath, value);
      setLocalStorageObject('pros-preferences', cookiePreferences);
      preferencesStore.setPreferences(cookiePreferences);
      return Promise.resolve(cookiePreferences);
    }
  }

  function setOrgPreferenceAsync(path, value, context = null) {
    return setPreferenceAsync(path, value, context, true, false);
  }

  function setOrgTypePreferenceAsync(path, value, context = null) {
    return setPreferenceAsync(path, value, context, false, true);
  }

  function getPathWithContext(path, context) {
    return context ? `${context}.${path}` : path;
  }

  function getPathWithOrg(path) {
    return `${services.session.getContextId()}.${path}`;
  }

  function getPathWithOrgType(path) {
    return `${services.session.getOrgType()}.${path}`;
  }

  function deletePreferenceAsync(path, context = null, withOrg = false, withOrgType = false) {
    let myPath = withOrg ? getPathWithOrg(path) : path;
    myPath = withOrgType ? getPathWithOrgType(myPath) : myPath;
    myPath = getPathWithContext(myPath, context);

    let fullPath = `preferences/${myPath}`;
    if (services.session.hasEntity()) {
      return services.http.delete('portal_service', fullPath, {version: 2}).then(resp => {
        preferencesStore.setPreferences(resp.data);
        return resp.data;
      });
    }
    else {
      let cookiePreferences = getLocalStorageObject('pros-preferences', DEFAULT_PREFERENCES);
      services.helpers.set(cookiePreferences, myPath, undefined);
      cookiePreferences = JSON.parse(JSON.stringify(cookiePreferences));
      setLocalStorageObject('pros-preferences', cookiePreferences);
      preferencesStore.setPreferences(cookiePreferences);
      return Promise.resolve(cookiePreferences);
    }
  }

  function deleteOrgPreferenceAsync(path, context = null) {
    return deletePreferenceAsync(path, context, true);
  }

  function deleteOrgTypePreferenceAsync(path, context = null) {
    return deletePreferenceAsync(path, context, false, true);
  }

  // Adapted from http://stackoverflow.com/a/15724300/277307
  function getCookie(name, subName = null) {
    let cookie = {};
    document.cookie.split(';').forEach(c => {
      let parts = c.split('=');
      let cookieName = parts[0].trim();
      let cookieValue = decodeURIComponent(parts.slice(1).join('='));
      cookie[cookieName] = {
        value: cookieValue
      };
      let subValues = {};
      if (cookieValue.indexOf('&') > 0) {
        let valueParts = cookieValue.split('&');
        valueParts.forEach(p => {
          let part = p.split('=');
          if (part.length > 1) {
            subValues[part[0].trim()] = decodeURIComponent(part[1]);
          }
        });
      }
      cookie[cookieName].subValues = subValues;
    });

    if (!cookie[name]) {
      return null;
    }

    return subName ? cookie[name].subValues[subName] : cookie[name].value;
  }

  function deleteCookie(name) {
    let cookieValue = '; path=/';
    cookieValue += '; domain=';
    cookieValue += window.location.hostname;
    cookieValue += ";expires=Thu, 01 Jan 1970 00:00:01 GMT";
    document.cookie = `${name}=${cookieValue}`;
  }

  function setCookie(name, value) {
    let cookieValue = `${encodeURIComponent(value)}`;
    cookieValue += '; path=/';
    cookieValue += '; domain=';
    cookieValue += window.location.hostname;
    document.cookie = `${name}=${cookieValue}`;
  }

  function getCookieObject(name, defaultValue = null) {
    let cookie = getCookie(name);
    return cookie ? JSON.parse(cookie) : defaultValue;
  }

  function setCookieObject(name, value) {
    setCookie(name, JSON.stringify(value));
  }

  function setLocalStorage(name, value) {
    localStorage.setItem(name, value);
  }

  function setLocalStorageObject(name, object) {
    setLocalStorage(name, JSON.stringify(object));
  }

  function getLocalStorage(name, defaultValue = null) {
    let item = localStorage.getItem(name);
    return item ? item : defaultValue;
  }

  function getLocalStorageObject(name, defaultValue = null) {
    let item = localStorage.getItem(name);
    if (item) {
      item = JSON.parse(item);
    }
    return item ? item : defaultValue;
  }

  function deleteLocalStorage(name) {
    localStorage.removeItem(name);
  }
}
